import React from 'react';
import { useTranslation } from 'react-i18next';

import { RoleContext } from '@/app/api/common';
import { store, useAppSelector } from '@/app/store';
import { FirmwareInfo } from '@/features/firmware/firmwaresSlice';
import { FirmwareDescriptor, copyFirmwareDescriptors } from '@/features/projects/projectsSlice';

import Dropdown, { FieldInfo } from '@/components/Common/Dropdown';
import FlexRow from '@/components/Common/FlexRow';

// Map platform value to display name
const platformDisplayNames: Record<string, string> = {
  'tizen': 'Tizen',
  'android': 'Android',
  'webos': 'WebOS',
  'philips': 'Philips',
};

export const getPlatformDisplayName = (platform: string): string => {
  return platformDisplayNames[platform.toLowerCase()] || platform;
};

export interface FirmwareRowProps {
  firmware: FirmwareDescriptor;
  firmwares: FirmwareInfo[];
  setFirmware: (firmware: FirmwareDescriptor) => void;
  requestDelete: () => void;
  triedSave?: boolean;
}

export const FirmwareRow = (props: FirmwareRowProps) => {
  const { t } = useTranslation();

  const selectedPlatform = props.firmware?.platform || '';
  const selectedModel = props.firmware?.model || '';
  const selectedVersion = props.firmware?.version || '';

  // Get unique platforms from firmwares (show all platforms)
  const platformOpts = React.useMemo((): FieldInfo[] => {
    if (!props.firmwares || props.firmwares.length === 0) return [];
    const platforms = [...new Set(props.firmwares.map((f: FirmwareInfo) => f.platform))] as string[];
    return platforms.map(p => ({ value: p, desc: getPlatformDisplayName(p) }));
  }, [props.firmwares]);

  // Get models filtered by selected platform
  const modelOpts = React.useMemo((): FieldInfo[] => {
    if (!props.firmwares || !selectedPlatform) return [];
    const filteredFirmwares = props.firmwares.filter((f: FirmwareInfo) => f.platform === selectedPlatform);
    const models = [...new Set(filteredFirmwares.map((f: FirmwareInfo) => f.model))] as string[];
    return models.map(m => ({ value: m, desc: m }));
  }, [props.firmwares, selectedPlatform]);

  // Get versions filtered by selected platform and model
  // Use update.version instead of versions array - this is the target firmware version
  const versionOpts = React.useMemo((): FieldInfo[] => {
    if (!props.firmwares || !selectedPlatform || !selectedModel) return [];
    // Filter all firmwares matching platform and model
    const filteredFirmwares = props.firmwares.filter(
      (f: FirmwareInfo) => f.platform === selectedPlatform && f.model === selectedModel
    );
    if (filteredFirmwares.length === 0) return [];
    // Collect all update.version values from matching firmwares
    const versions = filteredFirmwares
      .filter((f: FirmwareInfo) => f.update?.version)
      .map((f: FirmwareInfo) => f.update.version);
    // Remove duplicates and return as options
    const uniqueVersions = [...new Set(versions)] as string[];
    return uniqueVersions.map((v: string) => ({ value: v, desc: v }));
  }, [props.firmwares, selectedPlatform, selectedModel]);

  // Update firmware
  const updateFirmware = React.useCallback((platform: string, model: string, version: string) => {
    props.setFirmware({ platform, model, version });
  }, [props.setFirmware]);

  // Handlers for dropdown changes
  const handlePlatformChange = React.useCallback((val: string) => {
    updateFirmware(val, '', '');
  }, [updateFirmware]);

  const handleModelChange = React.useCallback((val: string) => {
    updateFirmware(selectedPlatform, val, '');
  }, [updateFirmware, selectedPlatform]);

  const handleVersionChange = React.useCallback((val: string) => {
    updateFirmware(selectedPlatform, selectedModel, val);
  }, [updateFirmware, selectedPlatform, selectedModel]);

  return (
    <>
      <Dropdown label={t('projects:project:project_settings:firmware:platform')}
        value={selectedPlatform}
        placeholder={t('projects:project:project_settings:firmware:select_platform')}
        options={platformOpts}
        setValue={handlePlatformChange}>
        <></>
      </Dropdown>
      {selectedPlatform && (
        <Dropdown label={t('projects:project:project_settings:firmware:model') + '*'}
          value={selectedModel}
          placeholder={t('projects:project:project_settings:firmware:select_model')}
          options={modelOpts}
          isInvalid={props.triedSave && selectedPlatform !== '' && selectedModel === ''}
          errorMessage={t('projects:project:project_settings:firmware:model_invalid')}
          setValue={handleModelChange}>
          <></>
        </Dropdown>
      )}
      {selectedModel && (
        <Dropdown label={t('projects:project:project_settings:firmware:version') + '*'}
          value={selectedVersion}
          placeholder={t('projects:project:project_settings:firmware:select_version')}
          options={versionOpts}
          isInvalid={props.triedSave && selectedModel !== '' && selectedVersion === ''}
          errorMessage={t('projects:project:project_settings:firmware:version_invalid')}
          setValue={handleVersionChange}>
          <></>
        </Dropdown>
      )}
      <FlexRow flex={0}
               className="gap-[5px] cursor-pointer text-[--Primetime-main]"
               onClick={() => props.requestDelete()}>
        <span className="material-icons !text-[--Primetime-main] text-[16px]">
          remove
        </span>
        {t('projects:project:project_settings:firmware:remove')}
      </FlexRow>
    </>
  );
};

export interface FirmwaresEditorProps {
  firmwares: FirmwareDescriptor[];
  setFirmwares: (firmwares: FirmwareDescriptor[]) => void;
  triedSave?: boolean;
  defaultPlatform?: string; // If provided, auto-set this platform when adding new firmware
  singleEntry?: boolean; // If true, only allow one firmware entry (for room level)
}

const FirmwaresEditor = (props: FirmwaresEditorProps) => {
  const { t } = useTranslation();
  const role = React.useContext(RoleContext);

  const firmwareList = useAppSelector((state) => state.firmwares.firmwares);

  // Fetch firmwares on mount (only if user has permission)
  React.useEffect(() => {
    if (!firmwareList || firmwareList.length === 0) {
      if (role.admin || role?.privilages?.includes("ViewFirmware")) {
        store.getFirmwares();
      }
    }
  }, []);

  // Find the matching platform name for default (case-insensitive match)
  const matchedPlatform = React.useMemo(() => {
    if (!props.defaultPlatform || !firmwareList) return '';
    const match = firmwareList.find((f: FirmwareInfo) =>
      f.platform.toLowerCase() === props.defaultPlatform?.toLowerCase()
    );
    return match ? match.platform : props.defaultPlatform;
  }, [firmwareList, props.defaultPlatform]);


  const updateFirmware = React.useCallback((i: number, firmware: FirmwareDescriptor) => {
    if (!props.firmwares) return;

    let firmwares = copyFirmwareDescriptors(props.firmwares);
    firmwares[i] = firmware;

    props.setFirmwares(firmwares);
  }, [props.firmwares, props.setFirmwares]);

  const deleteFirmware = React.useCallback((i: number) => {
    if (!props.firmwares) return;

    let firmwares = copyFirmwareDescriptors(props.firmwares);
    firmwares.splice(i, 1);

    props.setFirmwares(firmwares);
  }, [props.firmwares, props.setFirmwares]);

  const addFirmware = React.useCallback(() => {
    let firmwares = props.firmwares ? copyFirmwareDescriptors(props.firmwares) : [];
    // Auto-set platform if platformFilter is provided
    firmwares.push({
      platform: matchedPlatform || '',
      model: '',
      version: '',
    });

    props.setFirmwares(firmwares);
  }, [props.firmwares, props.setFirmwares, matchedPlatform]);

  // Don't show anything if no firmwares available
  if (!firmwareList || firmwareList.length === 0) {
    return (
      <FlexRow className="text-[14px] text-[--Dark-Grey]">
        {t('projects:project:project_settings:firmware:no_firmware_for_platform')}
      </FlexRow>
    );
  }

  // For singleEntry mode, check if we already have an entry
  const canAddMore = !props.singleEntry || !props.firmwares || props.firmwares.length === 0;

  return (
    <React.Fragment>
      {props.firmwares && props.firmwares.map((firmware: FirmwareDescriptor, i: number) =>
        <FirmwareRow key={i}
                     firmware={firmware}
                     firmwares={firmwareList}
                     setFirmware={(newFirmware: FirmwareDescriptor) => updateFirmware(i, newFirmware)}
                     triedSave={props.triedSave}
                     requestDelete={() => deleteFirmware(i)} />
      )}
      {canAddMore && (
        <FlexRow flex={0}
                 className="gap-[5px] cursor-pointer text-[--Primetime-main]"
                 onClick={() => addFirmware()}>
          <span className="material-icons !text-[--Primetime-main] text-[16px]">
            add
          </span>
          {t('projects:project:project_settings:firmware:add')}
        </FlexRow>
      )}
    </React.Fragment>
  );
};

export default FirmwaresEditor;
